import i18nConfig from 'i18n.config';
import { unstable_cache } from 'next/cache';
import { client } from './contentfulClient';

export interface Path {
  tags: any;
  current: string;
  title: string;
  id: string;
}

// promise style
// In-memory cache
const cache: Record<string, Path[]> = {};

export const getPaths = async (pageType: string = 'page'): Promise<Path[]> => {
  let start = performance.now();

  // Check if result is cached
  if (cache[pageType]) {
    let end = performance.now();
    console.log('=== GET PATHS PERFORMANCE (From Cache)', end - start);
    return cache[pageType];
  }

  const results = await client().getEntries({
    content_type: pageType,
    limit: 1000,
  });

  const paths = results.items.map((page) => {
    // TODO: map Contentful types into TS types
    return {
      // @ts-ignore
      current: String(page.fields.slug) || '',
      // @ts-ignore
      title: String(page.fields.title) || '',
      id: String(page.sys.id) || '',
      tags: page.metadata.tags || [],
      locale: String(page.fields.locale) || '',
    };
  });

  // Cache the result
  cache[pageType] = paths;

  let end = performance.now();
  console.log('=== GET PATHS PERFORMANCE', end - start);
  return paths;
};

export const getContentfulAbsolutePath = (
  seoOpenImage: string | { fields: { file: { url: string } } },
): string => {
  const url = typeof seoOpenImage === 'string' ? seoOpenImage : seoOpenImage?.fields?.file?.url;
  return url?.startsWith('//images.ctfassets.net/') || url?.startsWith('//downloads.ctfassets.net/')
    ? `https:${url}?w=1200&h=630&fit=fill`
    : url;
};

// async await style
export const getPageData = async (
  locale: string,
  slug: string,
  preview?: boolean,
): Promise<any> => {
  const fetchCachedFooterData: any = unstable_cache(
    async () =>
      client().getEntries({
        'fields.slug': slug,
        content_type: 'page',
        include: 5,
        locale: fixLocale(locale),
      }),
    [`page-${locale}-${slug}`],
    { tags: ['page'] },
  );
  const clientEntries = await fetchCachedFooterData();
  return clientEntries;
};

export const getAllPages = async (locale: string): Promise<any> => {
  const clientEntries = await client().getEntries({
    content_type: 'page',
    include: 5,
    locale: fixLocale(locale),
  });
  return clientEntries;
};

// async await style
export const getShopPageData = async (
  locale: string,
  slug: string,
  contentfulTags: string,
  preview?: boolean,
): Promise<any> => {
  // Query entries by tag ID
  const clientEntries = await client().getEntries({
    'fields.slug': slug,
    content_type: 'pageShopVersion',
    include: 10,
    locale: fixLocale(locale),
    'metadata.tags.sys.id[in]': [contentfulTags],
  });

  return clientEntries;
};

export const getAllShopPages = async (locale: string, contentfulTags: string): Promise<any> => {
  // Query entries by tag ID
  const clientEntries = await client().getEntries({
    content_type: 'pageShopVersion',
    locale: fixLocale(locale),
    'metadata.tags.sys.id[in]': [contentfulTags],
  });

  return clientEntries;
};

export const getContentTypePageData = async (
  locale: string,
  slug: string,
  contentType: string,
  preview?: boolean,
  field?: string,
): Promise<any> => {
  const clientEntries = await client().getEntries({
    'fields.slug': slug,
    content_type: contentType,
    include: 8,
    limit: 1,
    select: field ? [`fields.${field}`] : undefined,
    locale: fixLocale(locale),
  });
  return clientEntries;
};

export function uppercaseAfterDash(str) {
  if (!str.includes('-')) return str;
  return str
    .split('-')
    .map((word, index) => {
      return index === 0 ? word : word.toUpperCase();
    })
    .join('-');
}
export function fixLocale(str) {
  if (str == null || str == 'en' || !i18nConfig.locales.includes(str)) {
    return 'en-US';
  }
  return uppercaseAfterDash(str);
}

export const getNeighborhoodsAllHomepageData = async (
  locale: string,
  shopname: string,
  preview?: boolean,
): Promise<any> => {
  const clientEntries = await client().getEntries({
    content_type: 'neighborhoodsAllHomepage',
    'fields.slug': shopname,
    limit: 1,
    include: 8,
    locale: fixLocale(locale),
  });
  return clientEntries;
};

// async await style
// call in async way
export const getFooterData = async (
  locale: string,
  id: string,
  preview?: boolean,
): Promise<any> => {
  const fetchCachedFooterData: any = unstable_cache(
    () =>
      client().getEntries({
        'sys.id': id,
        content_type: 'siteFooter',
        locale: fixLocale(locale),
      }),
    [`siteFooter-${locale}`],
    {
      tags: ['siteFooter'],
    },
  );
  const footerData = await fetchCachedFooterData();

  if (footerData) {
    let modifiedFooterData = {};
    footerData.items.map((item) => {
      if (item?.fields?.address || item.fields?.cityStateZip) {
        modifiedFooterData = {
          ...footerData,
          [item.sys?.locale]: locale,
        };
      } else {
        modifiedFooterData = {
          ...footerData,
          [item.sys?.locale]: fixLocale(locale),
        };
      }
    });

    return modifiedFooterData;
  }
};

export const getGlobalSiteHeaderData = async (locale: string, id: string): Promise<any> => {
  const fetchCachedGlobalSiteHeader = unstable_cache(
    () =>
      client().getEntries({
        'sys.id': id,
        content_type: 'globalSiteHeader',
        include: 5,
        locale: fixLocale(locale),
      }),
    [`globalSiteHeader-${locale}`],
    {
      tags: ['globalSiteHeader'],
    },
  );
  const globalSiteHeaderData = await fetchCachedGlobalSiteHeader();

  return globalSiteHeaderData;
};

export const getPaymentCalculatorData = async (locale: string, id: string): Promise<any> => {
  return await client().getEntries({
    'sys.id': id,
    content_type: 'paymentCalculator',
    include: 5,
    locale: fixLocale(locale),
  });
};

export const getWelcomeMessageDescription = async (id: string, locale: string): Promise<any> => {
  return await client().getEntry(id, { locale: fixLocale(locale) });
};

export const getTextAndImageBlockData = async (id: string, preview?: boolean): Promise<any> => {
  return await client().getEntries({
    'sys.id': id,
    content_type: 'textAndImageBlock',
  });
};

export const getValuePropCarouseData = async (): Promise<any> => {
  return await client().getEntries({
    content_type: 'valuePropCarousel',
  });
};

export const getValuePropCarouseDataALT = async (): Promise<any> => {
  return await client().getEntries({
    content_type: 'valuePropCarouselAlt',
    include: 5,
  });
};

export const getHeaderFooterData = async (locale: string): Promise<any> => {
  const results = await Promise.all([
    getGlobalSiteHeaderData(locale, '5oAGk0xOkB7c6AJlMiqkhO'),
    getFooterData(locale, '535GAkRuKeyRdqYF9UqwQX'),
  ]);

  return results;
};
